import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { SoftwareUpdateService } from './services/software-update.service';
import { SoftwareUpdate, DisplayTechnology, UploadSoftwareUpdateRequest } from './models/software-update.model';
import { Grid } from '../../shared/components/grid/grid';
import { GridPage } from '../../shared/components/grid/grid-page';
import { GridCellDirective } from '../../shared/components/grid/directives/grid-cell.directive';
import {
  GridColumn,
  GridRowAction,
  GridSort,
  GridTab,
  gridColumns,
} from '../../shared/components/grid/models/grid.model';
import { ToastService } from '../../shared/services/toast-service';
import { BreadcrumbService } from '../../shared/components/breadcrumbs/breadcrumb.service';
import { UploadSoftwareUpdateDialogComponent } from './upload-dialog/upload-dialog.component';
import { SoftwareUpdateDetailDialogComponent } from './detail-dialog/detail-dialog.component';

@Component({
  selector: 'app-software-updates',
  standalone: true,
  imports: [
    Grid,
    GridPage,
    GridCellDirective,
    UploadSoftwareUpdateDialogComponent,
    SoftwareUpdateDetailDialogComponent,
  ],
  templateUrl: './software-updates.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoftwareUpdatesComponent implements OnInit {
  private readonly service = inject(SoftwareUpdateService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly breadcrumbs = inject(BreadcrumbService);

  readonly updates = signal<SoftwareUpdate[]>([]);
  readonly page = signal(0);
  readonly size = signal(20);
  readonly totalElements = signal(0);
  readonly totalPages = signal(0);
  readonly sort = signal<GridSort>({ sortBy: 'createdAt', sortDirection: 'DESC' });
  readonly isLoading = signal(false);

  /** Technology filter, driven by the grid's tabs ('' = All). */
  readonly activeTab = signal<DisplayTechnology | ''>('');

  readonly isDialogOpen = signal(false);
  readonly isDetailDialogOpen = signal(false);
  readonly detailUpdate = signal<SoftwareUpdate | null>(null);

  /** Stable row id for tracking. */
  readonly updateId = (u: SoftwareUpdate) => u.id;
  /** Narrows the erased-generic row that projected grid templates receive. */
  readonly asUpdate = (row: unknown): SoftwareUpdate => row as SoftwareUpdate;

  /** All / one-per-technology tabs; replaces the old technology <select>. */
  readonly tabs: GridTab[] = [
    { id: '', label: 'All' },
    { id: 'EPAPER', label: 'E-Paper' },
    { id: 'LCD', label: 'LCD' },
    { id: 'LED', label: 'LED' },
  ];

  readonly columns: GridColumn<SoftwareUpdate>[] = gridColumns<SoftwareUpdate>([
    { key: 'name', label: 'Name', type: 'text', textVariant: 'strong', sortable: true, alwaysVisible: true },
    { key: 'version', label: 'Version', type: 'text', sortable: true, width: 130 },
    { key: 'displayTechnology', label: 'Technology', type: 'text', sortable: true, width: 150 },
    { key: 'size', label: 'Size', type: 'template', template: 'size', sortable: false, width: 120, value: (row) => row.size },
    { key: 'uploadedBy', label: 'Uploaded By', type: 'text', sortable: false },
    { key: 'createdAt', label: 'Created', type: 'date', sortable: true, width: 180 },
  ]);

  /** Row overflow-menu actions — replaces the old right-click options menu. */
  readonly rowActions: GridRowAction<SoftwareUpdate>[] = [
    { id: 'view', label: 'View Details', placement: 'menu' },
    { id: 'assign-device', label: 'Assign to Devices', placement: 'menu' },
    { id: 'delete', label: 'Delete', placement: 'menu', danger: true },
  ];

  ngOnInit(): void {
    this.fetchUpdates();
  }

  fetchUpdates(): void {
    this.isLoading.set(true);
    const tech = this.activeTab() || undefined;

    this.service.getAll(tech).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        const data = response.data;
        this.updates.set(data);
        // The list endpoints aren't paged; derive a best-effort page count so
        // pagination stays sane (one extra page while a full page comes back).
        const totalPages = data.length === this.size() ? this.page() + 2 : this.page() + 1;
        this.totalPages.set(totalPages);
        this.totalElements.set(this.page() * this.size() + data.length);
        this.isLoading.set(false);
      },
      error: () => {
        this.toastService.show('Failed to load software updates', 'error');
        this.isLoading.set(false);
      },
    });
  }

  onTabChange(id: string): void {
    this.activeTab.set(id as DisplayTechnology | '');
    this.page.set(0);
    this.fetchUpdates();
  }

  onSortChange(sort: GridSort): void {
    this.sort.set(sort);
    this.page.set(0);
    this.fetchUpdates();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.fetchUpdates();
  }

  onPageSizeChange(size: number): void {
    this.size.set(size);
    this.page.set(0);
    this.fetchUpdates();
  }

  onRowAction(actionId: string, update: SoftwareUpdate): void {
    switch (actionId) {
      case 'view':
        this.detailUpdate.set(update);
        this.isDetailDialogOpen.set(true);
        break;
      case 'assign-device':
        // Multi-device, cross-tenant assignment now lives on the update's
        // own detail page (see detail/software-update-detail.component.ts).
        // Name the crumb before navigating so the trail never flashes a raw
        // id — same pattern as the admin tenants list.
        this.breadcrumbs.setLabel('id', update.id, `${update.name} ${update.version}`);
        this.router.navigate(['/admin/software-updates', update.id]);
        break;
      case 'delete':
        this.deleteUpdate(update.id);
        break;
    }
  }

  onViewDialogClose(): void {
    this.isDetailDialogOpen.set(false);
    this.detailUpdate.set(null);
    this.fetchUpdates();
  }

  openDialog(): void {
    this.isDialogOpen.set(true);
  }

  closeDialog(): void {
    this.isDialogOpen.set(false);
  }

  handleUpload(req: UploadSoftwareUpdateRequest): void {
    this.service.upload(req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show('Software update uploaded', 'success');
          this.closeDialog();
          this.fetchUpdates();
        },
        error: () => this.toastService.show('Upload failed', 'error'),
      });
  }

  deleteUpdate(id: string): void {
    this.service.delete(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show('Software update deleted', 'success');
          this.fetchUpdates();
        },
        error: () => this.toastService.show('Failed to delete software update', 'error'),
      });
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }
}
